const ZENDESK_SNIPPET_ID = "ze-snippet";
const ZENDESK_SNIPPET_SRC =
  "https://static.zdassets.com/ekr/snippet.js?key=d5fb427c-718a-47f4-b88e-e03d643cc822";

class ZendeskWidgetLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ZendeskWidgetLoadError";
  }
}

type ZendeskCommand = (...args: unknown[]) => void;

declare global {
  interface Window {
    zE?: ZendeskCommand;
    zESettings?: Record<string, unknown>;
  }
}

let zendeskReadyPromise: Promise<void> | null = null;

const getUserName = () => localStorage.getItem("username") || "";

const getUserEmail = () => {
  return (
    localStorage.getItem("email") || localStorage.getItem("username") || ""
  );
};

const parseName = (fullName: string) => {
  if (!fullName) {
    return { fname: "", lname: "" };
  }

  const parts = fullName.split(" ");
  const fname = parts[0] || "";
  const lname = parts.slice(1).join(" ") || "";

  return { fname, lname };
};

const buildZendeskSettings = (username: string) => {
  const nameParts = parseName(username);

  return {
    webWidget: {
      launcher: {
        visible: false,
      },
      contactForm: {
        fields: [
          { id: "6316035058957", prefill: { "*": nameParts.fname } },
          { id: 6316016942221, prefill: { "*": nameParts.lname } },
        ],
      },
    },
  };
};

const waitForZendeskReady = () => {
  if (typeof window.zE !== "undefined") {
    return Promise.resolve();
  }

  if (zendeskReadyPromise) {
    return zendeskReadyPromise;
  }

  zendeskReadyPromise = new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();
    const checkInterval = window.setInterval(() => {
      if (typeof window.zE !== "undefined") {
        window.clearInterval(checkInterval);
        resolve();
        return;
      }

      if (Date.now() - startedAt > 10000) {
        window.clearInterval(checkInterval);
        zendeskReadyPromise = null;
        reject(new ZendeskWidgetLoadError("Zendesk widget load timeout"));
      }
    }, 100);
  });

  return zendeskReadyPromise;
};

const appendZendeskSnippet = () => {
  const existingScript = document.getElementById(
    ZENDESK_SNIPPET_ID
  ) as HTMLScriptElement | null;

  if (existingScript) {
    return;
  }

  const script = document.createElement("script");
  script.id = ZENDESK_SNIPPET_ID;
  script.src = ZENDESK_SNIPPET_SRC;
  script.async = true;
  script.onerror = () => {
    zendeskReadyPromise = null;
  };
  document.head.appendChild(script);
};

const applyZendeskIdentity = () => {
  const username = getUserName();
  const email = getUserEmail();
  const settings = buildZendeskSettings(username);

  window.zESettings = settings;

  if (typeof window.zE === "undefined") {
    return;
  }

  window.zE("webWidget", "updateSettings", settings);

  if (email) {
    window.zE("webWidget", "identify", {
      name: username,
      email,
    });
    window.zE("webWidget", "prefill", {
      email: {
        value: email,
        readOnly: true,
      },
    });
  }
};

export const ensureZendeskWidget = async () => {
  try {
    window.zESettings = buildZendeskSettings(getUserName());
    appendZendeskSnippet();
    await waitForZendeskReady();
    applyZendeskIdentity();
  } catch (error) {
    if (error instanceof ZendeskWidgetLoadError) {
      return;
    }

    throw error;
  }
};
